<section class="config-section">
    <h3 class="config-section-title">Markets</h3>

    <p class="config-counter config-note">
        A market is a named group of services, used for business rules, reporting and access.
        Each market holds one or more blocks of services &mdash; a block can be an RSID range or
        a single service, scoped to a set of days.
    </p>

    <div class="markets-toolbar">
        <button kendoButton (click)="addMarket()"><i class="fas fa-plus toolbar-icon"></i>Add Market</button>
        <button kendoButton (click)="importMarkets()"><i class="fas fa-file-import toolbar-icon"></i>Import</button>
        <button kendoButton (click)="exportMarkets()"><i class="fas fa-file-export toolbar-icon"></i>Export</button>
        <button kendoButton (click)="openPriorityOrder()"><i class="fas fa-sort toolbar-icon"></i>Priority Order</button>
        <div class="spacer"></div>
        <span class="dirty-note" *ngIf="isDirty">Unsaved changes &mdash; service counts refresh after save.</span>
        <button kendoButton [primary]="true" [disabled]="!isDirty || saving" (click)="save()">
            <span *ngIf="saving" class="k-i-loading saving-icon"></span>{{ saving ? 'Saving...' : 'Save' }}
        </button>
    </div>

    <!-- History link comes later, alongside the change-history feature itself. -->
    <div class="summary" *ngIf="unassigned">
        <span><b class="num">{{ marketCount }}</b> market{{ marketCount === 1 ? '' : 's' }}</span>
        <span class="sep" *ngIf="unassigned"></span>
        <span *ngIf="unassigned">
            <b class="num">{{ unassigned.total - unassigned.count }}</b> of {{ unassigned.total }} services assigned
        </span>
        <span class="sep" *ngIf="unassigned"></span>
        <span *ngIf="unassigned">
            <b class="num">{{ unassigned.count }}</b> {{ unassigned.name | lowercase }}
        </span>
        <span class="meta" *ngIf="lastChanged">
            Last changed by {{ lastChanged.user }} on {{ lastChanged.dttm | date:'dd/MM/yyyy' }}
        </span>
    </div>

    <div class="markets-grid-wrap" *ngIf="unassigned">
        <kendo-grid [data]="gridData" [scrollable]="'none'" [rowClass]="rowClass">
            <kendo-grid-column field="service" title="Market / Services" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="pinned-label" *ngIf="dataItem.isPinnedRow">
                        Everything else &rarr; <strong>{{ dataItem.market }}</strong>
                    </span>

                    <ng-container *ngIf="dataItem.isEditRow && draft">
                        <div class="edit-panel">
                            <div class="ep-row">
                                <div class="ep-field">
                                    <label>Services from</label>
                                    <input class="ep-input mono" [(ngModel)]="draft.rsidFrom" name="rsidFrom" placeholder="GR100000" />
                                </div>
                                <div class="ep-field">
                                    <label>to</label>
                                    <input class="ep-input mono" [(ngModel)]="draft.rsidTo" name="rsidTo" placeholder="GR199999" />
                                </div>
                                <div class="ep-field ep-days">
                                    <label>When</label>
                                    <span class="day-pills">
                                        <button type="button" class="day-pill day-toggle" *ngFor="let day of allDays"
                                                [ngClass]="'day-' + day.toLowerCase()"
                                                [class.day-active]="draft.days.has(day)"
                                                (click)="toggleDraftDay(day)">{{ day }}</button>
                                    </span>
                                    <span class="ep-hint inline" *ngIf="!draft.days.size">No days picked = every day.</span>
                                </div>
                            </div>

                            <!-- Positioned to reach the table's true right edge (see .ep-actions) - not
                                 in normal flow, so it plays no part in this column's own width. -->
                            <div class="ep-actions">
                                <button kendoButton (click)="cancelEdit(dataItem.marketRef, dataItem.blockRef)">Cancel</button>
                                <button kendoButton [primary]="true" (click)="commitEdit(dataItem.marketRef, dataItem.blockRef)">Done</button>
                            </div>

                            <div class="ep-hint">Assigning a single service? Set from and to to the same RSID.</div>

                            <div class="disclose">
                                <a href="#" class="link"
                                   (click)="draft.dateRangeEnabled = !draft.dateRangeEnabled; $event.preventDefault()">
                                    <i class="fas fa-chevron-right" [class.expanded]="draft.dateRangeEnabled"></i>
                                    Limit to a date range
                                </a>
                                <div class="dbody" *ngIf="draft.dateRangeEnabled">
                                    <div class="ep-field">
                                        <label>From</label>
                                        <input type="date" class="ep-input" [(ngModel)]="draft.dateFrom" name="dateFrom" />
                                    </div>
                                    <div class="ep-field">
                                        <label>To</label>
                                        <input type="date" class="ep-input" [(ngModel)]="draft.dateTo" name="dateTo" />
                                    </div>
                                    <div class="ep-hint">
                                        Use a date range only for a temporary change. Without one, the block
                                        applies to every departure date &mdash; including past ones, which moves
                                        reported history.
                                    </div>
                                </div>
                            </div>

                            <div class="ep-error" *ngIf="draftError">
                                <i class="fas fa-times-circle"></i> {{ draftError }}
                            </div>
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!dataItem.isPinnedRow && !dataItem.isEditRow">
                        <ng-container *ngIf="dataItem.isGroupRow; else serviceCell">
                            <a href="#" class="group-toggle" (click)="toggleMarket(dataItem.market); $event.preventDefault()">
                                <i class="fas fa-chevron-right caret" [class.expanded]="expandedMarkets.has(dataItem.market)"></i>
                                <span class="group-name">{{ dataItem.market }}</span>
                                <span class="group-meta">
                                    {{ dataItem.itemCount }} block{{ dataItem.itemCount === 1 ? '' : 's' }}
                                </span>
                            </a>
                        </ng-container>
                        <ng-template #serviceCell>
                            <span class="mono">{{ dataItem.service }}</span>
                            <i class="fas fa-exclamation-triangle overlap-badge" *ngIf="dataItem.overlapCount"
                               [title]="'Overlaps ' + dataItem.overlapCount + ' other rule' + (dataItem.overlapCount === 1 ? '' : 's') + ' — resolved by priority order'"></i>
                        </ng-template>
                    </ng-container>
                </ng-template>
            </kendo-grid-column>

            <kendo-grid-column field="days" title="When" [width]="260" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <ng-container *ngIf="!dataItem.isGroupRow && !dataItem.isPinnedRow && !dataItem.isEditRow">
                        <span class="day-pills">
                            <span *ngIf="!dataItem.days" class="day-pill day-all">All days</span>
                            <span *ngFor="let day of dataItem.days" class="day-pill" [ngClass]="'day-' + day.toLowerCase()">{{ day }}</span>
                        </span>
                        <span class="date-window" *ngIf="dataItem.dateFrom">
                            {{ dataItem.dateFrom | date:'dd/MM/yyyy' }} &ndash; {{ dataItem.dateTo | date:'dd/MM/yyyy' }}
                        </span>
                    </ng-container>
                </ng-template>
            </kendo-grid-column>

            <kendo-grid-column field="covered" title="Services" [width]="110" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="covered-count" *ngIf="!dataItem.isEditRow"
                          [class.zero]="coveredIsZero(dataItem)"
                          [class.unknown]="coveredIsUnknown(dataItem)"
                          [class.unassigned-count]="dataItem.isPinnedRow && dataItem.covered">
                        {{ coveredDisplay(dataItem) }}
                    </span>
                </ng-template>
            </kendo-grid-column>

            <kendo-grid-column field="actions" title="" [width]="110" [headerClass]="'ra-grid-header'">
                <ng-template kendoGridCellTemplate let-dataItem>
                    <span class="row-actions" *ngIf="!dataItem.isPinnedRow && !dataItem.isEditRow">
                        <ng-container *ngIf="dataItem.isGroupRow; else ruleActions">
                            <i class="fas fa-plus" title="Add block" (click)="addBlock(dataItem.marketRef)"></i>
                            <i class="fas fa-pencil-alt" title="Rename market" (click)="renameMarket(dataItem.marketRef)"></i>
                            <i class="fas fa-trash-alt" title="Delete market" (click)="deleteMarket(dataItem.marketRef)"></i>
                        </ng-container>
                        <ng-template #ruleActions>
                            <i class="fas fa-pencil-alt" title="Edit" (click)="editBlock(dataItem.blockRef)"></i>
                            <i class="fas fa-trash-alt" title="Delete" (click)="deleteBlock(dataItem.marketRef, dataItem.blockRef)"></i>
                        </ng-template>
                    </span>
                </ng-template>
            </kendo-grid-column>
        </kendo-grid>
    </div>

    <div class="count" *ngIf="unassigned">
        {{ marketCount }} market{{ marketCount === 1 ? '' : 's' }}
        &middot; {{ serviceCount }} block{{ serviceCount === 1 ? '' : 's' }}
    </div>
</section>
